import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { pennerManagementApi } from "../../../services/api/omni/pennermanagement";
import {
  buildPennerCacheKey,
  getPennerCache,
} from "../../../services/api/omni/pennerManagementCache";
import PennerSearchField from "../components/PennerSearchField";
import PennerTablePagination, {
  DEFAULT_PAGE_SIZE,
} from "../components/PennerTablePagination";
import { useDebouncedValue } from "../utils/useDebouncedValue";

const PERIOD_OPTIONS = [
  { label: "Yesterday", value: "yesterday" },
  { label: "7D", value: "7d" },
  { label: "30D", value: "30d" },
  { label: "Q1", value: "q1" },
  { label: "Q2", value: "q2" },
  { label: "Q3", value: "q3" },
  { label: "Q4", value: "q4" },
];

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === "string") return detail;
    return JSON.stringify(detail);
  }
  return error.message || "Unbekannter Fehler";
};

const SalesTab = ({ onError }) => {
  const [period, setPeriod] = useState("30d");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState({
    total_gross_revenue: 0,
    order_count: 0,
    lines: [],
  });

  useEffect(() => {
    setPage(0);
  }, [period, debouncedSearch, pageSize]);

  const fetchSales = useCallback(async () => {
    const query = {
      period,
      search: debouncedSearch,
      skip: page * pageSize,
      limit: pageSize,
    };
    const cacheKey = buildPennerCacheKey("sales", query);
    const cached = getPennerCache(cacheKey);
    if (cached) {
      setSalesData({
        total_gross_revenue: cached.total_gross_revenue ?? 0,
        order_count: cached.order_count ?? 0,
        lines: cached.lines ?? [],
      });
    }

    try {
      if (!cached) {
        setLoading(true);
      }
      onError?.(null);
      const response = await pennerManagementApi.getSales(query);
      setSalesData({
        total_gross_revenue: response.data?.total_gross_revenue ?? 0,
        order_count: response.data?.order_count ?? 0,
        lines: response.data?.lines ?? [],
      });
    } catch (err) {
      console.error("Failed to fetch penner sales", err);
      onError?.(getErrorMessage(err));
      setSalesData({ total_gross_revenue: 0, order_count: 0, lines: [] });
    } finally {
      setLoading(false);
    }
  }, [period, debouncedSearch, page, pageSize, onError]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const lines = salesData.lines ?? [];
  const total = salesData.order_count ?? 0;
  const hasSearch = Boolean(searchQuery.trim());
  const isEmpty = !loading && lines.length === 0;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [total, page, pageSize]);

  return (
    <Box>
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          What is counted here?
        </Typography>
        <Typography variant="body2" component="div">
          Only sales of <strong>Penner articles</strong> are counted (
          <code>article_type = penner</code> with at least one entry in{" "}
          <code>pricing_history</code>). Order lines from{" "}
          <strong>Amazon</strong> and <strong>Plenty</strong> in the selected
          period are matched by SKU/ASIN. The gross revenue sums the gross
          values of these lines.
        </Typography>
      </Alert>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          alignItems: "center",
        }}
      >
        {PERIOD_OPTIONS.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            clickable
            color={period === option.value ? "primary" : "default"}
            variant={period === option.value ? "filled" : "outlined"}
            onClick={() => setPeriod(option.value)}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        <PennerSearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search SKU, source (amazon/plenty) or marketplace…"
        />
        {!loading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: "center" }}
          >
            {total.toLocaleString("de-DE")} lines
          </Typography>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Gross Revenue
              </Typography>
              <Typography variant="h4" fontWeight="medium">
                {loading ? (
                  <CircularProgress size={28} />
                ) : (
                  formatCurrency(salesData.total_gross_revenue)
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Order Lines (Penner)
              </Typography>
              <Typography variant="h5" fontWeight="medium">
                {loading ? "—" : total.toLocaleString("de-DE")}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && lines.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isEmpty ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {hasSearch
              ? "No sales match your search"
              : "No sales in the selected period"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasSearch
              ? "Try another SKU, marketplace (e.g. DE) or source (amazon, plenty)."
              : "No matching order lines were found for Penner articles in this period."}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Article</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Marketplace</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Gross Amount</TableCell>
                  <TableCell align="right">Reduction Waves</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow
                    key={`${line.sku}-${line.order_date}-${index}`}
                    hover
                  >
                    <TableCell>{line.sku ?? "—"}</TableCell>
                    <TableCell>{line.articlename ?? "—"}</TableCell>
                    <TableCell>{formatDate(line.order_date)}</TableCell>
                    <TableCell>{line.order_source ?? "—"}</TableCell>
                    <TableCell>
                      {line.marketplace ?? line.origin ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      {line.quantity != null
                        ? Number(line.quantity).toLocaleString("de-DE")
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(line.gross_amount)}
                    </TableCell>
                    <TableCell align="right">
                      {line.reduction_waves ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <PennerTablePagination
            count={total}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={setPage}
            onRowsPerPageChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default SalesTab;
